import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import {
  signIn,
  signOut,
  getCurrentUser,
  fetchAuthSession,
  confirmSignIn,
  resetPassword,
  confirmResetPassword,
} from "aws-amplify/auth";
import { configureAmplifyAuth } from "@/lib/amplify-config";
import {
  resolveTenantConfig,
  type TenantResolutionError,
} from "@/lib/tenant-resolver";
import { useAuthStore } from "@/stores/auth-store";
import { resolveShell } from "@/routes";
import {
  AuthContext,
  type AuthContextValue,
  type MfaSetupData,
} from "@/contexts/auth-context";
import type { AuthUser, Role } from "@/types/auth";

async function parseAuthUser(
  tenantId: string,
  tenantName: string,
  tenantType: "hcp" | "clinical",
): Promise<AuthUser> {
  const session = await fetchAuthSession();
  const idToken = session.tokens?.idToken;

  const payload = idToken?.payload ?? {};
  const groups = (payload["cognito:groups"] as string[] | undefined) ?? [];
  const role = (groups[0] ?? "tecnico") as Role;
  const email = (payload["email"] as string) ?? "";
  const sub = (payload["sub"] as string) ?? "";

  return { sub, email, role, tenantId, tenantName, tenantType };
}

function getTenantDefaults(shell: "admin" | "patient" | "tenant") {
  switch (shell) {
    case "admin":
      return {
        tenantId: "superadmin",
        tenantName: "OHTIS Admin",
        tenantType: "hcp" as const,
      };
    case "patient":
      return {
        tenantId: "patient",
        tenantName: "OHTIS Patient",
        tenantType: "hcp" as const,
      };
    case "tenant":
      return {
        tenantId: "",
        tenantName: "",
        tenantType: "hcp" as const,
      };
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const store = useAuthStore();
  const tenantErrorRef = useRef<TenantResolutionError | null>(null);
  const initRef = useRef(false);
  const [mfaSetupData, setMfaSetupData] = useState<MfaSetupData | null>(null);

  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;

    async function init() {
      store.setInitializing(true);

      const result = await resolveTenantConfig(window.location.hostname);

      if (!result.ok) {
        tenantErrorRef.current = result.error;
        store.setInitializing(false);
        store.setStep("login");
        return;
      }

      const { cognitoConfig, tenant } = result.data;

      store.setCognitoConfig(cognitoConfig);

      if (tenant) {
        store.setTenantContext(tenant);
      }

      configureAmplifyAuth(cognitoConfig.userPoolId, cognitoConfig.clientId);

      try {
        await getCurrentUser();

        const { shell } = resolveShell(window.location.hostname);
        const defaults = getTenantDefaults(shell);
        const user = await parseAuthUser(
          tenant?.tenantId ?? defaults.tenantId,
          tenant?.tenantName ?? defaults.tenantName,
          tenant?.tenantType ?? defaults.tenantType,
        );
        store.setAuth(user);
      } catch {
        store.setStep("login");
      } finally {
        store.setInitializing(false);
      }
    }

    init();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const login = useCallback(
    async (email: string, password: string) => {
      store.setError(null);

      try {
        const result = await signIn({ username: email, password });

        if (result.nextStep.signInStep === "DONE") {
          const { shell } = resolveShell(window.location.hostname);
          const defaults = getTenantDefaults(shell);
          const user = await parseAuthUser(
            store.tenantId ?? defaults.tenantId,
            store.tenantName ?? defaults.tenantName,
            store.tenantType ?? defaults.tenantType,
          );
          store.setAuth(user);
          return;
        }

        if (
          result.nextStep.signInStep ===
          "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED"
        ) {
          store.setStep("force_change_password");
          return;
        }

        if (
          result.nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_TOTP_CODE" ||
          result.nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_SMS_CODE"
        ) {
          store.setStep("mfa_challenge");
          return;
        }

        if (
          result.nextStep.signInStep === "CONTINUE_SIGN_IN_WITH_TOTP_SETUP"
        ) {
          const totpSetupDetails = result.nextStep.totpSetupDetails;
          const secretCode = totpSetupDetails.sharedSecret;
          const qrUri = totpSetupDetails.getSetupUri("OHTIS").toString();
          setMfaSetupData({ secretCode, qrUri });
          store.setStep("mfa_setup");
          return;
        }
      } catch (error) {
        const message =
          error instanceof Error ? error.message : String(error);
        store.setError(message);
        throw error;
      }
    },
    [store],
  );

  const logout = useCallback(async () => {
    try {
      await signOut();
    } finally {
      store.clearAuth();
    }
  }, [store]);

  const completeNewPassword = useCallback(
    async (newPassword: string) => {
      store.setError(null);

      try {
        const result = await confirmSignIn({
          challengeResponse: newPassword,
        });

        if (result.nextStep.signInStep === "DONE") {
          const { shell } = resolveShell(window.location.hostname);
          const defaults = getTenantDefaults(shell);
          const user = await parseAuthUser(
            store.tenantId ?? defaults.tenantId,
            store.tenantName ?? defaults.tenantName,
            store.tenantType ?? defaults.tenantType,
          );
          store.setAuth(user);
          return;
        }

        if (
          result.nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_TOTP_CODE" ||
          result.nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_SMS_CODE"
        ) {
          store.setStep("mfa_challenge");
          return;
        }

        if (
          result.nextStep.signInStep === "CONTINUE_SIGN_IN_WITH_TOTP_SETUP"
        ) {
          const totpSetupDetails = result.nextStep.totpSetupDetails;
          const secretCode = totpSetupDetails.sharedSecret;
          const qrUri = totpSetupDetails.getSetupUri("OHTIS").toString();
          setMfaSetupData({ secretCode, qrUri });
          store.setStep("mfa_setup");
          return;
        }
      } catch (error) {
        const message =
          error instanceof Error ? error.message : String(error);
        store.setError(message);
        throw error;
      }
    },
    [store],
  );

  const forgotPassword = useCallback(
    async (email: string) => {
      store.setError(null);

      try {
        await resetPassword({ username: email });
        store.setStep("forgot_password_confirm");
      } catch (error) {
        const message =
          error instanceof Error ? error.message : String(error);
        store.setError(message);
        throw error;
      }
    },
    [store],
  );

  const confirmForgotPasswordFn = useCallback(
    async (email: string, code: string, newPassword: string) => {
      store.setError(null);

      try {
        await confirmResetPassword({
          username: email,
          confirmationCode: code,
          newPassword,
        });
        store.setStep("login");
      } catch (error) {
        const message =
          error instanceof Error ? error.message : String(error);
        store.setError(message);
        throw error;
      }
    },
    [store],
  );

  const confirmMfaCode = useCallback(
    async (code: string) => {
      store.setError(null);

      try {
        const result = await confirmSignIn({ challengeResponse: code });

        if (result.nextStep.signInStep === "DONE") {
          const { shell } = resolveShell(window.location.hostname);
          const defaults = getTenantDefaults(shell);
          const user = await parseAuthUser(
            store.tenantId ?? defaults.tenantId,
            store.tenantName ?? defaults.tenantName,
            store.tenantType ?? defaults.tenantType,
          );
          store.setAuth(user);
        }
      } catch (error) {
        const message =
          error instanceof Error ? error.message : String(error);
        store.setError(message);
        throw error;
      }
    },
    [store],
  );

  const confirmMfaSetup = useCallback(
    async (code: string) => {
      store.setError(null);

      try {
        const result = await confirmSignIn({ challengeResponse: code });

        if (result.nextStep.signInStep === "DONE") {
          setMfaSetupData(null);
          const { shell } = resolveShell(window.location.hostname);
          const defaults = getTenantDefaults(shell);
          const user = await parseAuthUser(
            store.tenantId ?? defaults.tenantId,
            store.tenantName ?? defaults.tenantName,
            store.tenantType ?? defaults.tenantType,
          );
          store.setAuth(user);
        }
      } catch (error) {
        const message =
          error instanceof Error ? error.message : String(error);
        store.setError(message);
        throw error;
      }
    },
    [store],
  );

  const value: AuthContextValue = {
    login,
    logout,
    completeNewPassword,
    forgotPassword,
    confirmForgotPassword: confirmForgotPasswordFn,
    confirmMfaCode,
    confirmMfaSetup,
    mfaSetupData,
    tenantError: tenantErrorRef.current,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
